import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Compass, Plane } from "lucide-react";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    const res = isLogin ? await login(email, password) : await register(name, email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(isLogin ? "Welcome back!" : "Account created");
      nav("/");
    } else {
      toast.error(res.error);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left visual panel */}
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1503431153573-96e959f4d9b7?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/80 via-primary/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-white/15 grid place-items-center backdrop-blur">
              <Compass className="w-5 h-5" />
            </div>
            <div className="font-display text-xl">TravelVault</div>
          </div>
          <div>
            <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tight">
              Every ticket, every receipt, every traveler — one calm home.
            </h1>
            <p className="mt-4 text-white/80 max-w-md">
              Organize flights, hotels, car rentals and vital traveler info for individuals, families and groups.
            </p>
          </div>
          <div className="flex items-center gap-2 text-white/80 text-sm">
            <Plane className="w-4 h-4" /> Trip-ready in under a minute
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <Card className="w-full max-w-md p-8 rounded-2xl border-border">
          <h2 className="font-display text-2xl font-medium tracking-tight" data-testid="auth-title">
            {isLogin ? "Welcome back" : "Create your vault"}
          </h2>
          <p className="text-sm text-stone-500 mt-1">
            {isLogin ? "Sign in to continue planning." : "Start organizing your trips today."}
          </p>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required
                       className="rounded-xl mt-1" placeholder="Jane Traveler" data-testid="name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                     className="rounded-xl mt-1" placeholder="you@example.com" data-testid="email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                     className="rounded-xl mt-1" placeholder="At least 6 characters" data-testid="password-input" />
              {isLogin && (
                <div className="mt-1 text-right">
                  <Link to="/forgot-password" className="text-xs text-stone-500 hover:text-primary" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
              )}
            </div>
            <Button type="submit" disabled={busy}
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:-translate-y-0.5 transition-transform"
                    data-testid="submit-btn">
              {busy ? "Please wait..." : isLogin ? "Sign in" : "Create account"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-stone-600">
            {isLogin ? (
              <>New here? <Link to="/register" className="text-primary font-medium" data-testid="register-link">Create an account</Link></>
            ) : (
              <>Have an account? <Link to="/login" className="text-primary font-medium" data-testid="login-link">Sign in</Link></>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
